import { formatUrlValue } from "@/helpers/jobs";
import { FC, ReactNode } from "react";

type JobInfoProps = {
  jobData: jobDescriptionType;
  isMobile: boolean;
};

const renderInfoItem = (label: string, value: ReactNode) =>
  value && (
    <p className="flex gap-1 text-sm">
      <span className="text-content-primary font-medium">{label}</span>
      <span className="text-content-secondary font-normal">{value}</span>
    </p>
  );

const splitItems = (items: string[]) =>
  items.flatMap((item) =>
    item.includes("/")
      ? item
          .split("/")
          .flatMap((subItem, index, array) =>
            index < array.length - 1 ? [subItem.trim(), "/"] : [subItem.trim()],
          )
      : [item.trim()],
  );

const anchorTag = (category: string[], shouldSplit = true) => {
  const formattedItems = shouldSplit ? splitItems(category) : category;

  return formattedItems?.map((item, index) => {
    const nextItem = formattedItems[index + 1];
    const isLastItem = index === formattedItems.length - 1;
    const separator =
      nextItem === "/" || item === "/" || isLastItem ? "" : ", ";

    return (
      <a
        key={index}
        href={`/search/${formatUrlValue(item)}-jobs`}
        className="text-content-primary cursor-pointer text-xs md:!text-sm"
      >
        {item}
        {separator}
      </a>
    );
  });
};

const JobInfo: FC<JobInfoProps> = ({ jobData, isMobile }) => {
  const { roles = "", industries, functions, jobTypes } = jobData;
  const roleText = Array.isArray(roles) ? anchorTag(roles) : "";
  const industriesText = Array.isArray(industries) ? anchorTag(industries) : "";
  const functionsText = Array.isArray(functions)
    ? anchorTag(functions, false)
    : "";
  const jobTypesText = Array.isArray(jobTypes) ? anchorTag(jobTypes) : "";

  if (!(roleText || industriesText || functionsText || jobTypesText))
    return null;

  return (
    <div
      className={`mt-4 flex flex-col gap-3 py-4 pl-4 md:!mt-0 md:!py-6 md:!pl-6${isMobile ? "border-surface-tertiary border-t border-solid" : ""}`}
    >
      <h2 className="text-content-primary text-base font-bold leading-6 md:!text-lg md:!leading-[26px]">
        More Info
      </h2>
      <div className="flex flex-col gap-1">
        {renderInfoItem("Role:", roleText)}
        {renderInfoItem("Industry:", industriesText)}
        {renderInfoItem("Function:", functionsText)}
        {renderInfoItem("Job Type:", jobTypesText)}
      </div>
    </div>
  );
};

export default JobInfo;
